import ApplicantSerializer from '@/utilities/serializers/applicant';
import BeneficiarySerializer from '@/utilities/serializers/beneficiary';
import Application from '@/models/onboarding/application.model';
import ApplicationList from '@/models/onboarding/application-list.model';

export default class ApplicationSerializer {
  static serialize(model) {
    // console.log('ApplicationSerializer#serialize', arguments);

    // application
    const application = {};
    application.externalId = model.id;
    application.applicantIpAddress = model.applicantIpAddress;
    application.name = `${model.primaryApplicant.firstName} ${model.primaryApplicant.lastName}`;
    application.email = model.primaryApplicant.email;
    application.promotionCode = model.promotionCode;
    application.platformId = model.platformId;
    application.professional = model.professional;

    // ach profile
    application.achProfile = model.achProfile || undefined;

    // agreements (only populated on submit)
    application.agreements = model.agreements;

    // ppc
    if (model.cache.signedPartnerAgreement) {
      application.paymentPlanCode = model.cache.ppc.paymentPlanCode;
    }

    // cache
    application.cache = model.cache || {};
    application.cache.stepName = model.stepName;
    application.cache.maxStepIndex = model.maxStepIndex;
    application.cache.foreignPrimaryIdUuid = model.foreignPrimaryIdUuid;
    application.cache.foreignPrimarySignatureUuid = model.foreignPrimarySignatureUuid;
    application.cache.foreignPrimaryAddressVerificationUuid = model.foreignPrimaryAddressVerificationUuid;

    application.cache.foreignSecondaryIdUuid = model.foreignSecondaryIdUuid;
    application.cache.foreignSecondarySignatureUuid = model.foreignSecondarySignatureUuid;
    application.cache.foreignSecondaryAddressVerificationUuid = model.foreignSecondaryAddressVerificationUuid;

    // meta
    application.meta = JSON.stringify(model.meta);

    // applicationType
    application.applicationType = {};
    application.applicationType.type = model.type;

    // applicationType : options
    if (model.hasOptions) {
      application.options = true;
      application.applicationType.optionAgreementForm = {};
      application.applicationType.optionAgreementForm.optionApproval = model.optionApproval;
    }

    // crypto
    if (model.hasCrypto) {
      application.crypto = true;
    }

    // futures
    application.futures = model.futures;
    application.futuresAccountForm = model.futuresAccountForm;
    if (application.futures && model.isIra) {
      application.futuresAccountForm.iraCustodian = 'Equity Trust';
    }

    // applicationType : trustedContactForm
    if (model.primaryApplicant.cache.hasTrustedContact) {
      application.applicationType.trustedContactForm = {};
      application.applicationType.trustedContactForm.givenName = model.trustedContactGivenName;
      application.applicationType.trustedContactForm.familyName = model.trustedContactFamilyName;
      application.applicationType.trustedContactForm.emailAddress = model.trustedContactEmailAddress;
      application.applicationType.trustedContactForm.phoneNumber = {
        phoneNumber: model.trustedContactPhoneNumber,
        phoneNumberType: 'HOME',
      };
    }

    // applicationType : custodialAgreementForm
    if (model.isCustodial) {
      application.applicationType.custodialAgreementForm = {};
      application.applicationType.custodialAgreementForm.custodianType = model.custodianType;
      application.applicationType.custodialAgreementForm.custodialState = model.custodialState;
    }

    // applicationType: retirementAccountBeneficiariesForm
    if (model.isIra) {
      application.applicationType.retirementAccountBeneficiariesForm = {};
      application.applicationType.retirementAccountBeneficiariesForm.beneficiaries = model.beneficiaries.map((b) =>
        BeneficiarySerializer.serialize(b)
      );

      // applicationType : iraSepRolloverAgreementForm
      application.applicationType.iraSepRolloverAgreementForm = {};
      application.applicationType.iraSepRolloverAgreementForm.typeIRA = model.typeIRA;
    }

    // applicationType : foreignDueDiligenceForm
    if (model.isForeign) {
      application.applicationType.foreignDueDiligenceForm = {};
      application.applicationType.foreignDueDiligenceForm.businessNature = model.primaryApplicant.isEmployed
        ? model.businessNature
        : 'UNEMPLOYED';
      application.applicationType.foreignDueDiligenceForm.primaryBanking = [model.primaryBanking];
      application.applicationType.foreignDueDiligenceForm.expectedWithdrawals = model.expectedWithdrawals;
      application.applicationType.foreignDueDiligenceForm.initialDepositType = model.initialDepositType;
      application.applicationType.foreignDueDiligenceForm.valueInitialDeposit = model.valueInitialDeposit;

      application.applicationType.foreignDueDiligenceForm.referredToBroker = model.referredToBroker ? 'YES' : 'NO';
      if (model.referredToBroker) {
        application.applicationType.foreignDueDiligenceForm.referredName = model.platformId;
        application.applicationType.foreignDueDiligenceForm.referredRelationship = 'Platform';
      } else {
        application.applicationType.foreignDueDiligenceForm.initialContact = model.initialContact;
      }
    }

    // newAccountForm, newAccountIraForm, newAccountForeignForm
    const formKey = model.formKey;
    application.applicationType[formKey] = {};

    // form
    application.applicationType[formKey].accountType = model.accountType.toUpperCase();
    application.applicationType[formKey].customerType = model.customerType;

    // applicants
    application.applicationType[formKey].applicants = [];
    application.applicationType[formKey].applicants.push(ApplicantSerializer.serialize(model.primaryApplicant));
    if (model.isCustodial || model.isJoint) {
      application.applicationType[formKey].applicants.push(ApplicantSerializer.serialize(model.secondaryApplicant));
    }

    // form : investmentProfile
    application.applicationType[formKey].investmentProfile = {};
    application.applicationType[formKey].investmentProfile.investmentExperience = model.investmentExperience;
    application.applicationType[formKey].investmentProfile.investmentObjective = model.investmentObjective;
    application.applicationType[formKey].investmentProfile.riskTolerance = model.riskTolerance;
    application.applicationType[formKey].investmentProfile.stockExperience = model.stockExperience;
    application.applicationType[formKey].investmentProfile.optionExperience = model.optionExperience;
    application.applicationType[formKey].investmentProfile.totalNetWorthUSD = model.totalNetWorthUSD;
    application.applicationType[formKey].investmentProfile.annualIncomeUSD = model.annualIncomeUSD;

    return application;
  }

  static deserialize(data) {
    // console.log('ApplicationSerializer#deserialize', data);
    if (Array.isArray(data)) {
      return ApplicationSerializer.deserializeArray(data);
    }

    return ApplicationSerializer.deserializeObject(data);
  }

  static deserializeArray(jsonArray) {
    // console.log('ApplicationSerializer#deserializeArray', arguments);
    // TODO: adjust backend to return a normal REST payload
    return new ApplicationList(jsonArray.map((el) => ApplicationSerializer.deserializeObject(el)));
  }

  static deserializeObject(json) {
    // console.log('ApplicationSerializer#deserializeObject', arguments);
    const applicationPayload = json.application;
    const application = {};
    const type = applicationPayload.applicationType;
    const form = type.newAccountForm || type.newAccountIraForm || type.newAccountForeignForm;

    application.id = applicationPayload.externalId;
    application.promotionCode = applicationPayload.promotionCode;
    application.platformId = applicationPayload.platformId;
    application.paymentPlanCode = applicationPayload.paymentPlanCode;
    application.professional = applicationPayload.professional;

    // account
    application.accountKind = ApplicationSerializer.parseAccountKind(type.type);
    application.accountType = (form.accountType || '').toLowerCase();
    // application.customerType = form.customerType COMPUTED
    // application.typeIRA = form.typeIRA COMPUTED

    // investment profile
    application.investmentExperience = form.investmentProfile?.investmentExperience;
    application.investmentObjective = form.investmentProfile?.investmentObjective;
    application.riskTolerance = form.investmentProfile?.riskTolerance;
    application.stockExperience = form.investmentProfile?.stockExperience;
    application.optionExperience = form.investmentProfile?.optionExperience;
    application.totalNetWorthUSD = form.investmentProfile?.totalNetWorthUSD;
    application.annualIncomeUSD = form.investmentProfile?.annualIncomeUSD;

    // options
    application.options = type.optionAgreementForm?.optionApproval !== 0;
    application.optionApproval = type.optionAgreementForm?.optionApproval || 0;

    // crypto
    application.crypto = applicationPayload.crypto || false;

    // futures
    application.futures = applicationPayload.futures || false;
    application.futuresAccountForm = applicationPayload.futuresAccountForm;

    // foreign
    application.initialContact = type.foreignDueDiligenceForm?.initialContact;
    application.businessNature = type.foreignDueDiligenceForm?.businessNature;
    application.primaryBanking = type.foreignDueDiligenceForm?.primaryBanking[0] || '';
    application.expectedWithdrawals = type.foreignDueDiligenceForm?.expectedWithdrawals;
    application.initialDepositType = type.foreignDueDiligenceForm?.initialDepositType;
    application.valueInitialDeposit = type.foreignDueDiligenceForm?.valueInitialDeposit;

    // custodial
    application.custodianType = type.custodialAgreementForm?.custodianType;
    application.custodialState = type.custodialAgreementForm?.custodialState;

    // trusted contact
    application.trustedContactGivenName = type.trustedContactForm?.givenName;
    application.trustedContactFamilyName = type.trustedContactForm?.familyName;
    application.trustedContactEmailAddress = type.trustedContactForm?.emailAddress;
    application.trustedContactPhoneNumber = type.trustedContactForm?.phoneNumber?.phoneNumber;

    // relationships
    application.applicants = (form.applicants || []).map((o, index) => {
      const applicant = ApplicantSerializer.deserializeObject(o, application.id);
      // merge the verifications into the applicant
      if (applicationPayload.verifications && applicationPayload.verifications[index]) {
        const verification = applicationPayload.verifications[index] || null;
        if (verification) applicant.verification = verification;
      }
      return applicant;
    });
    application.beneficiaries = (type.retirementAccountBeneficiariesForm?.beneficiaries || []).map((o) =>
      BeneficiarySerializer.deserializeObject(o, application.id)
    );

    // achProfile
    application.achProfile = applicationPayload.achProfile;

    // cache
    application.cache = applicationPayload.cache;
    application.stepName = applicationPayload.cache?.stepName;
    application.maxStepIndex = applicationPayload.cache?.maxStepIndex;
    application.foreignPrimaryIdUuid = applicationPayload.cache?.foreignPrimaryIdUuid;
    application.foreignPrimarySignatureUuid = applicationPayload.cache?.foreignPrimarySignatureUuid;
    application.foreignPrimaryAddressVerificationUuid = applicationPayload.cache?.foreignPrimaryAddressVerificationUuid;
    application.foreignSecondaryIdUuid = applicationPayload.cache?.foreignSecondaryIdUuid;
    application.foreignSecondarySignatureUuid = applicationPayload.cache?.foreignSecondarySignatureUuid;
    application.foreignSecondaryAddressVerificationUuid =
      applicationPayload.cache?.foreignSecondaryAddressVerificationUuid;

    // meta
    application.meta = JSON.parse(applicationPayload.meta);

    application.status = applicationPayload.status;
    application.verifications = applicationPayload.verifications;

    return new Application(application);
  }

  static parseAccountKind(type) {
    if (type.includes('individual')) {
      return 'individual';
    }

    if (type.includes('joint')) {
      return 'joint';
    }

    if (type.includes('custodial')) {
      return 'custodial';
    }

    if (type.includes('ira')) {
      return type;
    }
  }
}
